import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FileText, Upload, Clock, ShieldCheck, ArrowRight, CreditCard, Loader2, CheckCircle2 } from "lucide-react";
import { API } from "./shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const cfg = { withCredentials: true };
const fmt = (n) => "$" + Number(n || 0).toLocaleString();

const STAGES = [
  { key: "quote", label: "Quote" },
  { key: "po", label: "Purchase Order / Payment" },
  { key: "approval", label: "589 Labs Approval" },
  { key: "active", label: "Portfolio Active" },
];

export default function VendorPO() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState(null); // "card" | "po"
  const [redirecting, setRedirecting] = useState(false);
  const [poNumber, setPoNumber] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/onboarding/procurement`, cfg);
      setData(r.data);
    } catch { /* no-op */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Stripe return — poll status when session_id present
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const r = await axios.get(`${API}/api/onboarding/checkout/status/${sid}`, cfg);
        if (r.data.payment_status === "paid" || r.data.provisioned) {
          toast.success("Payment received — your portfolio is now active!");
          window.history.replaceState({}, "", "/portfolio/vendor-po");
          load();
          return;
        }
      } catch { /* retry */ }
      if (tries < 6) setTimeout(poll, 1500);
      else { window.history.replaceState({}, "", "/portfolio/vendor-po"); load(); }
    };
    poll();
  }, [load]);

  const quote = data?.quote;
  const po = data?.purchase_order;
  const pfStatus = data?.portfolio_status;
  const finalized = pfStatus === "Active" || quote?.status === "Provisioned";
  const stageIdx = finalized ? 3
    : (pfStatus === "Pending Approval" || po?.status === "under_review") ? 2
    : (po || quote?.payment_status === "po_submitted") ? 1 : 0;

  const payByCard = async () => {
    setRedirecting(true);
    try {
      const r = await axios.post(`${API}/api/onboarding/checkout`, { origin_url: window.location.origin }, cfg);
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setRedirecting(false);
    }
  };

  const uploadPO = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a PO file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("po_number", poNumber);
      await axios.post(`${API}/api/onboarding/purchase-order`, fd, cfg);
      toast.success("Purchase Order submitted — 589 Labs will review it shortly.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); } finally { setUploading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="vendor-po">
      <div className="mb-6">
        <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-400">MySchoolRx</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Vendor &amp; PO</h1>
        <p className="text-sm text-slate-500 mt-1">Complete payment for your authorizer portfolio order.</p>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
      ) : !quote ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center" data-testid="vendor-po-empty">
          <FileText className="h-7 w-7 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-600 font-medium">No order yet.</p>
          <p className="text-xs text-slate-500 mt-1 mb-4">Complete School Onboarding to generate a quote and begin the PO process.</p>
          <Button className="bg-red-700 hover:bg-red-800" onClick={() => (window.location.href = "/portfolio/onboarding")}>
            Start School Onboarding <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><div className="text-xs uppercase tracking-wide text-slate-400">Quote</div>
                <div className="text-lg font-bold text-slate-900" data-testid="vendor-po-quote-number">{quote.quote_number}</div>
                <div className="text-xs text-slate-500">{quote.selected_school_count} school(s)</div></div>
              <div className="text-right"><div className="text-xs uppercase tracking-wide text-slate-400">Annual Total</div>
                <div className="text-lg font-bold text-slate-900" data-testid="vendor-po-total">{fmt(quote.annual_total)}</div></div>
            </div>
          </div>

          {/* Procurement pipeline */}
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Procurement status</h3>
            <div className="space-y-3">
              {STAGES.map((s, i) => {
                const done = i <= stageIdx;
                return (
                  <div key={s.key} className="flex items-center gap-3">
                    <span className={`h-6 w-6 rounded-full grid place-items-center text-xs ${done ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-400"}`}>{i + 1}</span>
                    <span className={`text-sm ${done ? "text-slate-800 font-medium" : "text-slate-400"}`}>{s.label}</span>
                    <span className="ml-auto text-xs">{done
                      ? <span className="text-emerald-700">{i === stageIdx && !finalized ? "In progress" : "Done"}</span>
                      : <span className="inline-flex items-center gap-1 text-slate-400"><Clock className="h-3 w-3" /> Pending</span>}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {finalized ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="vendor-po-active">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto" />
              <p className="mt-2 text-[15px] font-semibold text-emerald-800">Your portfolio is active.</p>
              <p className="text-sm text-emerald-700 mt-1 mb-4">Open the Activation Center to add each school's contact and send invitations.</p>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => (window.location.href = "/portfolio/activation")} data-testid="vendor-po-goto-activation">
                Open Activation Center <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          ) : po && po.status === "under_review" ? (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-5 text-sm text-blue-800 flex items-start gap-2" data-testid="vendor-po-submitted">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">Purchase Order {po.po_number || "(no number)"} submitted.</div>
                589 Labs will review and approve it. Your portfolio will activate on approval.
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button onClick={() => setMethod("card")} className={`rounded-xl border p-5 text-left ${method === "card" ? "border-[#007a9e] bg-[#f0f9fc]" : "border-slate-200 hover:border-[#007a9e]/40"}`} data-testid="vendor-po-method-card">
                  <CreditCard className="h-6 w-6 text-[#00698a]" />
                  <div className="mt-2 text-[15px] font-bold text-slate-900">Pay by card</div>
                  <div className="text-[13px] text-slate-500">Secure Stripe checkout. Portfolio activates instantly.</div>
                </button>
                <button onClick={() => setMethod("po")} className={`rounded-xl border p-5 text-left ${method === "po" ? "border-[#007a9e] bg-[#f0f9fc]" : "border-slate-200 hover:border-[#007a9e]/40"}`} data-testid="vendor-po-method-po">
                  <FileText className="h-6 w-6 text-[#00698a]" />
                  <div className="mt-2 text-[15px] font-bold text-slate-900">Purchase Order</div>
                  <div className="text-[13px] text-slate-500">Upload a signed PO. We'll review and activate on acceptance.</div>
                </button>
              </div>

              {method === "card" && (
                <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="vendor-po-card-panel">
                  <Button disabled={redirecting} onClick={payByCard} className="w-full h-12 bg-[#007a9e] hover:bg-[#00607d] text-white text-[15px] font-semibold disabled:opacity-50" data-testid="vendor-po-pay-card-btn">
                    {redirecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Redirecting to Stripe…</> : `Pay ${fmt(quote.annual_total)} by card`}
                  </Button>
                </div>
              )}

              {method === "po" && (
                <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="vendor-po-po-panel">
                  <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500"><FileText className="h-4 w-4 inline mr-1" />Upload signed Purchase Order</div>
                  <Input placeholder="PO number (optional)" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} className="h-10" data-testid="vendor-po-number" />
                  <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.docx" className="block w-full text-[13px]" data-testid="vendor-po-file" />
                  <Button disabled={uploading} onClick={uploadPO} className="bg-[#007a9e] hover:bg-[#00607d] text-white disabled:opacity-50" data-testid="vendor-po-upload-btn">
                    {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading…</> : <><Upload className="h-4 w-4 mr-2" />Submit Purchase Order</>}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
